import { ResetIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSortSelect } from 'components/common/form/sort-select';
import { t } from 'i18next';
import { BPSortMode } from 'lib_ts/enums/bp-summary.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Competition } from 'lib_ts/enums/team.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { isNil, uniqBy } from 'lodash';
import { useMemo } from 'react';
import useBpSummaryStore from 'stores/use-bp-summary-store';

export const BpSummaryToolbar = () => {
  const summaries = useBpSummaryStore((s) => s.summaries);
  const filters = useBpSummaryStore((s) => s.filters);
  const sortMode = useBpSummaryStore((s) => s.sortMode);

  const machineOptions = useMemo(
    () =>
      uniqBy(
        summaries.map((s) => {
          const o: IOption = {
            group: `${s.competition}: ${s.teamName}`,
            label: MachineContextHelper.getMachineMenuLabel(s),
            value: s.machineID,
          };

          return o;
        }),
        (o) => o.value
      ),
    [summaries]
  );

  const teamOptions = useMemo(
    () =>
      uniqBy(
        summaries.map((s) => {
          const o: IOption = {
            group: s.competition,
            label: s.teamName,
            value: s.teamID,
          };

          return o;
        }),
        (o) => o.value
      ),
    [summaries]
  );

  return (
    <ErrorBoundary identifier="BpSummaryToolbar">
      <Flex gap={RADIX.FLEX.GAP.SM}>
        <Box>
          <CommonSortSelect
            identifier="PLSortSelect"
            label="Sort Options"
            value={sortMode}
            onChange={(v) => useBpSummaryStore.getState().setSortMode(v)}
            className="width-100p"
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            size={RADIX.BUTTON.SIZE.TABLE}
            color={RADIX.COLOR.NEUTRAL}
            options={Object.values(BPSortMode).map((v) => ({
              label: v,
              value: v,
            }))}
          />
        </Box>

        <Box flexGrow="1">
          <CommonSelectInput
            id="bp-summary-in-game"
            name="inGame"
            placeholder="common.status"
            options={[
              {
                label: 'In-Game',
                value: 'true',
              },
              {
                label: 'No Game',
                value: 'false',
              },
            ]}
            value={filters.inGame?.toString()}
            onOptionalBooleanChange={(v) =>
              useBpSummaryStore
                .getState()
                .mergeFilters({ inGame: isNil(v) ? null : v })
            }
            optional
          />
        </Box>

        <Box flexGrow="1">
          <CommonSearchInput
            id="bp-summary-competition"
            name="competition"
            placeholder="common.competition"
            options={Object.values(Competition).map((v) => ({
              label: v,
              value: v,
            }))}
            values={filters.competitions ?? []}
            onChange={(v) => {
              useBpSummaryStore.getState().mergeFilters({
                competitions: v.length === 0 ? null : (v as Competition[]),
              });
            }}
            skipSort
            optional
          />
        </Box>

        <Box flexGrow="1">
          <CommonSearchInput
            id="bp-summary-machineID"
            name="machineID"
            placeholder="admin.machines"
            options={machineOptions}
            values={filters.machineIDs ?? []}
            onChange={(v) => {
              useBpSummaryStore.getState().mergeFilters({
                machineIDs: v.length === 0 ? null : v,
              });
            }}
            multiple
            optional
          />
        </Box>

        <Box flexGrow="1">
          <CommonSearchInput
            id="bp-summary-team"
            name="team"
            placeholder="admin.teams"
            options={teamOptions}
            values={filters.teamIDs ?? []}
            onChange={(v) =>
              useBpSummaryStore
                .getState()
                .mergeFilters({ teamIDs: v.length === 0 ? null : v })
            }
            optional
          />
        </Box>

        <Box>
          <Button
            variant="soft"
            size={RADIX.BUTTON.SIZE.SM}
            color={RADIX.COLOR.NEUTRAL}
            onClick={() => useBpSummaryStore.getState().resetFilters()}
          >
            <ResetIcon /> {t('common.reset')}
          </Button>
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
